import React from "react";
import { Link } from "react-router-dom";
import { colors, fonts } from "../theme";
import { categories, values, assurances, marquee, contact } from "../data";
import { Eyebrow, Btn, Stat, CategoryCard, AssuranceCard, ValuesGrid, SectionHead, assuranceGrid } from "../ui";
import LogoMarquee from "../LogoMarquee";
import Seo from "../../seo/Seo";
import { PAGE_META } from "../../seo/pageMeta";
import {
  organizationSchema,
  websiteSchema,
  placeSchema,
  webPageSchema,
  itemListSchema,
} from "../../seo/schema";

const wrap = { maxWidth: "1180px", margin: "0 auto" };

export default function Home() {
  return (
    <main>
      {/* The home page carries the site-level entity graph: Organization,
          WebSite and Place. Every other page references these by @id rather
          than redeclaring them, so Google resolves one business, not many. */}
      <Seo
        title={PAGE_META.home.title}
        description={PAGE_META.home.description}
        path="/"
        schema={[
          organizationSchema(categories),
          websiteSchema(),
          placeSchema(),
          webPageSchema({
            path: "/",
            name: PAGE_META.home.title,
            description: PAGE_META.home.description,
          }),
          itemListSchema({
            path: "/",
            name: "Paper & board categories",
            items: categories.map((c) => ({
              name: c.name,
              path: `/catalogue/${c.slug}`,
              desc: c.blurb,
              image: c.image,
            })),
          }),
        ]}
      />
      {/* Hero — a full-bleed band rather than a boxed two-column grid. The
          photo is a wide product still with empty studio surface down its left
          side; the copy sits in that gap on desktop, and drops below the photo
          once the viewport is too narrow to hold both. */}
      <section className="se-hero">
        <div className="se-hero-media">
          {/* The LCP element on this page. Eager and high priority — it is the
              one image on the site that must never be lazy.

              The candidate list used to start at 1280w, so a 360px phone at 2x
              — needing 720 device pixels — had nothing to pick but a 19 KB
              file built for a laptop. 640w and 960w close that: the same phone
              now takes 7.3 KB, on the image its LCP is measured against.
              `src` stays 1920w, so a browser without srcset support is exactly
              where it was before. */}
          <img
            src="/assets/hero-1920.webp"
            srcSet="/assets/hero-640.webp 640w, /assets/hero-960.webp 960w, /assets/hero-1280.webp 1280w, /assets/hero-1920.webp 1920w, /assets/hero-2560.webp 2560w"
            sizes="100vw"
            width="1920"
            height="815"
            alt="Kraft paper roll, grey duplex board, fanned coloured sheets, tracing paper and peel-corner adhesive label stock arranged on a studio surface"
            fetchpriority="high"
            decoding="async"
          />
        </div>
        {/* Only the gutter goes fluid here: below 860px the stacked-hero rule
            in index.css replaces this padding shorthand outright, so the
            vertical values never reach a phone anyway. */}
        <div className="se-hero-copy" style={{ ...wrap, padding: "76px var(--sp-gutter) 46px" }}>
          <Eyebrow style={{ fontSize: "12px", letterSpacing: "0.16em" }}>
            Wholesale Paper Distributor · New Delhi
          </Eyebrow>
          <h1
            style={{
              fontFamily: fonts.serif,
              fontSize: "clamp(32px,6.5vw,54px)",
              lineHeight: 1.05,
              fontWeight: 500,
              margin: "20px 0 0",
              letterSpacing: "-0.01em",
            }}
          >
            A complete range of paper &amp; board, from one trusted source.
          </h1>
          <p style={{ fontSize: "17px", lineHeight: 1.6, color: colors.muted, maxWidth: "44ch", margin: "22px 0 0" }}>
            From copier reams and art card to kraft board and specialty stock — Sigma Enterprises supplies printers,
            converters and traders across India with dependable quality and trade pricing.
          </p>
          {/* Without a wrap the pair is 14px too wide for a 320-375px column,
              so both labels break mid-phrase ("View / Catalogue →"). */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "14px", marginTop: "32px" }}>
            <Btn variant="dark" to="/catalogue">View Catalogue →</Btn>
            <Btn variant="ghost" to="/contact">Request a Quote</Btn>
          </div>
          <div
            className="se-stats"
            style={{
              display: "flex",
              gap: "36px",
              marginTop: "44px",
              borderTop: `1px solid ${colors.border}`,
              paddingTop: "24px",
            }}
          >
            <Stat num="200+" label="Grades in stock" />
            <Stat num="20+" label="Product categories" />
            <Stat num="Pan-India" label="Supply & delivery" />
          </div>
        </div>
      </section>

      {/* Brand logo strip */}
      <LogoMarquee />

      {/* Marquee band */}
      <section style={{ borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}`, background: colors.panel }}>
        <div
          style={{
            ...wrap,
            padding: "16px var(--sp-gutter)",
            display: "flex",
            flexWrap: "wrap",
            gap: "10px 22px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontFamily: fonts.mono,
              fontSize: "var(--fs-label)",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: colors.brown,
              marginRight: "8px",
            }}
          >
            We supply
          </span>
          {marquee.map((m) => (
            <span key={m} style={{ fontSize: "var(--fs-meta)", color: colors.muted }}>{m}</span>
          ))}
        </div>
      </section>

      {/* Categories.
          Deliberately NOT marked `priority`. The LCP element on this page is
          the hero photo above, which is already eager and fetchpriority=high —
          it is the one image that should win the bandwidth race, and these
          cards must not compete with it. The hero section plus the logo strip
          and the marquee band push
          this grid roughly 900px down on desktop and further on mobile, so
          every card here is below the fold and should stay lazy. Eager-loading
          them would only steal bandwidth from the fonts the h1 needs. */}
      <section style={{ ...wrap, padding: "var(--sp-section-top) var(--sp-gutter) 20px" }}>
        {/* se-secthead drops this row to a column below 700px — the h2's
            min-content word plus the nowrap link do not fit a 320px line. */}
        <div className="se-secthead" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "32px", gap: "20px" }}>
          <SectionHead eyebrow="What we supply" title="Paper & board, organised by category" />
          {/* The link was a 19px-tall tap target; inline-flex + minHeight takes
              it to the 44px floor. The rule cannot ride on the <a> any more —
              on a 44px box a borderBottom lands 13px under the label — so it
              moves to an inset shadow on an inline span, which hugs the text
              the way the border used to. */}
          <Link
            to="/catalogue"
            style={{
              display: "inline-flex",
              alignItems: "center",
              minHeight: "44px",
              fontSize: "var(--fs-sm)",
              color: colors.brown,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ boxShadow: "inset 0 -1px 0 #C9B896" }}>Full catalogue →</span>
          </Link>
        </div>
        {/* min(260px,100%) stops the track floor exceeding the container: a
            bare 260px minimum left only 4px of headroom at 320px. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(260px,100%),1fr))", gap: "18px" }}>
          {categories.map((cat) => (
            <CategoryCard key={cat.slug} cat={cat} to={`/catalogue/${cat.slug}`} />
          ))}
        </div>
      </section>

      {/* Values */}
      <section style={{ ...wrap, padding: "var(--sp-section-top) var(--sp-gutter) 20px" }}>
        <ValuesGrid values={values} />
      </section>

      {/* Why the trade buys here */}
      <section style={{ ...wrap, padding: "clamp(36px,8vw,64px) var(--sp-gutter) 12px" }}>
        <Eyebrow>Why printers &amp; converters buy here</Eyebrow>
        <h2 style={{ fontFamily: fonts.serif, fontSize: "var(--fs-h2)", fontWeight: 500, margin: "10px 0 30px" }}>
          Supplying the trade from Delhi since 1996
        </h2>
        <div style={assuranceGrid}>
          {assurances.map((item) => (
            <AssuranceCard key={item.t} item={item} />
          ))}
        </div>
        <p style={{ fontSize: "var(--fs-body)", color: colors.muted, margin: "26px 0 0" }}>
          Customer reviews live on our{" "}
          {/* Same inline-link treatment as the identical sentence on /about:
              padding for a 44px target, negative margin to keep the line
              rhythm, and box-shadow instead of border-bottom because an
              inline border repaints on every wrapped fragment. */}
          <a
            href={contact.mapLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: colors.brown,
              display: "inline-block",
              padding: "13px 2px",
              margin: "-13px -2px",
              boxShadow: "inset 0 -1px 0 #C9B896",
            }}
          >
            Google Business Profile
          </a>
          , where they are verified and public.
        </p>
      </section>

      {/* CTA */}
      <section style={{ ...wrap, padding: "clamp(28px,6vw,40px) var(--sp-gutter) var(--sp-section-bot)" }}>
        <div
          className="se-cta"
          style={{
            background: colors.ink,
            borderRadius: "5px",
            padding: "56px 48px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "40px",
            flexWrap: "wrap",
          }}
        >
          <div>
            {/* 24 → 34px on the same 320/1280 line as the tokens; there is no
                shared token at this size, so the clamp is written out. */}
            <h2 style={{ fontFamily: fonts.serif, fontSize: "clamp(1.5rem, 1.25rem + 1.25vw, 2.125rem)", fontWeight: 500, color: colors.bg, margin: 0, maxWidth: "22ch" }}>
              Need a specific grade or grammage?
            </h2>
            <p style={{ fontSize: "var(--fs-body)", color: "#C4BBAB", margin: "14px 0 0", maxWidth: "48ch" }}>
              Send us your requirement and we'll get back with availability and a trade quote.
            </p>
          </div>
          <Btn variant="light" to="/contact" style={{ padding: "15px 28px", fontSize: "15px", fontWeight: 600, whiteSpace: "nowrap" }}>
            Send an enquiry →
          </Btn>
        </div>
      </section>
    </main>
  );
}
